/**
 * IdentityPhotoRequestsPanel — síndico review of identity (facial) photo requests.
 *
 * A resident either had no identity photo and sent one, or has one and asked for a troca. The
 * decision is made by looking at the two faces side by side, which is why every row renders the
 * CURRENT photo next to the PROPOSED one (`current_photo_url` vs `photo_url`); for a first photo
 * the left slot says so instead of showing a broken image.
 *
 * Approving installs the new photo as the approved + primary identity photo, retires the old one,
 * and updates the condomínio avatar. It does NOT push the readers — enrolment stays an explicit
 * sync — so the success toast says so rather than letting an operator assume the gate already
 * knows the new face.
 *
 * The account PROFILE photo is a different photo and is never touched here.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { X, CheckCircle, XCircle, Loader2, RefreshCw, Camera, Home, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  listIdentityPhotoRequests,
  approveIdentityPhotoRequest,
  rejectIdentityPhotoRequest,
  IdentityPhotoRequest,
} from '../../../services/identityPhotoRequestApi';
import { makeAbsolutePhotoUrl } from '../../../services/api';

interface IdentityPhotoRequestsPanelProps {
  open: boolean;
  customerId?: string;
  onClose: () => void;
}

interface IdentityPhotoRequestsListProps {
  customerId?: string;
  /** False while the host is closed, so a hidden panel does not poll the queue. */
  active?: boolean;
  /** Bumped by a host's own refresh button to force a reload. */
  reloadToken?: number;
  /** Lets a host render the count and the spinner in ITS chrome (the drawer header does). */
  onStateChange?: (state: { count: number; loading: boolean }) => void;
  /**
   * 'list' (default) is one card per row — right for the drawer, which is 576px wide.
   * 'grid' columns up on wide screens: on a 2560px monitor a single column of full-bleed cards
   * put the Aprovar button a screen-width away from the face it decides.
   */
  layout?: 'list' | 'grid';
}

/** Requests carry a resolved current photo the stored row does not have. */
type QueueItem = IdentityPhotoRequest & { current_photo_url?: string };

/**
 * The queue itself, with no chrome — the drawer below and the Solicitações page both render it,
 * so the two cannot drift into showing the same request differently.
 */
export function IdentityPhotoRequestsList({
  customerId,
  active = true,
  reloadToken = 0,
  onStateChange,
  layout = 'list',
}: IdentityPhotoRequestsListProps) {
  const [items, setItems] = useState<QueueItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [previewPhoto, setPreviewPhoto] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const resp = await listIdentityPhotoRequests(customerId, 'pending');
      setItems((resp.items || []) as QueueItem[]);
      setLoadError(null);
    } catch (err: any) {
      // Inline rather than a toast: swallowing it would render a 403 or a dropped connection as
      // "nothing pending", which is the one wrong answer a queue can give.
      setLoadError(err?.message || 'Erro ao carregar as solicitações de foto');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (active && customerId) load();
  }, [active, customerId, reloadToken, load]);

  useEffect(() => {
    onStateChange?.({ count: items.length, loading });
  }, [items.length, loading, onStateChange]);

  const handleApprove = async (item: QueueItem) => {
    if (!customerId) return;
    setProcessingId(item.id);
    try {
      const resp = await approveIdentityPhotoRequest(customerId, item.id);
      toast.success(resp.message || 'Foto aprovada');
      if (resp.device_sync_required) {
        // Said out loud: the photo is now the one of record, but the readers only learn it on the
        // next sync, and a síndico who assumes otherwise will be told the gate "is broken".
        // No icon — this codebase bans emoji in source, enforced by src/__tests__/noEmoji.test.ts.
        toast('Sincronize os dispositivos para enviar a nova foto aos leitores.');
      }
      setItems(prev => prev.filter(i => i.id !== item.id));
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao aprovar');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (item: QueueItem) => {
    if (!customerId) return;
    setProcessingId(item.id);
    try {
      await rejectIdentityPhotoRequest(customerId, item.id, rejectReason.trim() || undefined);
      toast.success('Solicitação rejeitada');
      setItems(prev => prev.filter(i => i.id !== item.id));
      setRejectingId(null);
      setRejectReason('');
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao rejeitar');
    } finally {
      setProcessingId(null);
    }
  };

  const personName = (item: QueueItem) =>
    `${item.person?.first_name || ''} ${item.person?.last_name || ''}`.trim() || 'Morador';
  const unitLabel = (item: QueueItem) =>
    item.unit ? `${item.unit.block?.name ? item.unit.block.name + ' / ' : ''}Ap ${item.unit.number}` : null;

  return (
    <>
      {previewPhoto && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90"
          onClick={() => setPreviewPhoto(null)}
        >
          <img
            src={previewPhoto}
            alt="Foto"
            className="max-w-xs max-h-[75vh] rounded-xl object-contain shadow-2xl"
          />
          <button
            className="absolute top-4 right-4 p-2 bg-gray-800/80 rounded-full text-gray-300 hover:text-white transition-colors"
            onClick={() => setPreviewPhoto(null)}
          >
            <X className="h-5 w-5" />
          </button>
          <p className="absolute bottom-6 text-xs text-gray-500">Clique para fechar</p>
        </div>
      )}

      <div
        className={
          layout === 'grid'
            ? 'grid gap-4 grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 items-start'
            : 'flex flex-col gap-4'
        }
      >
            {loadError && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-red-600 text-sm col-span-full">
                {loadError}
              </div>
            )}

            {!loadError && !loading && items.length === 0 && (
              <div className="flex flex-col items-center gap-2 py-16 text-center col-span-full">
                <Camera className="h-10 w-10 text-gray-300" />
                <p className="text-gray-500 text-sm">Nenhuma solicitação de foto pendente.</p>
              </div>
            )}

            {items.map(item => {
              const isProcessing = processingId === item.id;
              const isReplacement = Boolean(item.replaces_photo_id || item.current_photo_url);
              const currentUrl = item.current_photo_url ? makeAbsolutePhotoUrl(item.current_photo_url) : null;
              const proposedUrl = makeAbsolutePhotoUrl(item.photo_url);

              return (
                <div key={item.id} className="border border-gray-200 rounded-xl p-4 flex flex-col gap-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-gray-900">{personName(item)}</p>
                      {unitLabel(item) && (
                        <p className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                          <Home className="h-3 w-3" />
                          {unitLabel(item)}
                        </p>
                      )}
                    </div>
                    <span
                      className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${
                        isReplacement
                          ? 'bg-amber-500/10 text-amber-700'
                          : 'bg-blue-50 text-blue-700'
                      }`}
                    >
                      {isReplacement ? 'Troca de foto' : 'Primeira foto'}
                    </span>
                  </div>

                  {/* The decision: current face on the left, proposed on the right. */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex flex-col items-center gap-1">
                      <div className="h-24 w-20 rounded-lg bg-gray-50 border border-gray-200 overflow-hidden flex items-center justify-center">
                        {currentUrl ? (
                          <img
                            src={currentUrl}
                            alt={`Foto de identificação atual de ${personName(item)}`}
                            className="h-full w-full object-cover cursor-zoom-in"
                            onClick={() => setPreviewPhoto(currentUrl)}
                          />
                        ) : (
                          <span className="text-[10px] text-gray-400 text-center px-1">Sem foto</span>
                        )}
                      </div>
                      <span className="text-[10px] text-gray-400">Atual</span>
                    </div>

                    <ArrowRight className="h-4 w-4 text-gray-300 shrink-0" />

                    <div className="flex flex-col items-center gap-1">
                      <div className="h-24 w-20 rounded-lg bg-gray-50 border-2 border-blue-500/50 overflow-hidden">
                        <img
                          src={proposedUrl}
                          alt={`Foto enviada por ${personName(item)}`}
                          className="h-full w-full object-cover cursor-zoom-in"
                          onClick={() => setPreviewPhoto(proposedUrl)}
                        />
                      </div>
                      <span className="text-[10px] font-medium text-blue-600">Nova</span>
                    </div>

                    {item.reason && (
                      <p className="flex-1 text-xs text-gray-500 leading-relaxed italic">
                        “{item.reason}”
                      </p>
                    )}
                  </div>

                  {rejectingId === item.id ? (
                    <div className="flex flex-col gap-2">
                      <input
                        type="text"
                        value={rejectReason}
                        onChange={e => setRejectReason(e.target.value)}
                        placeholder="Motivo (opcional) — o morador recebe esta mensagem"
                        className="bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleReject(item)}
                          disabled={isProcessing}
                          className="flex-1 flex items-center justify-center gap-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white text-sm font-semibold rounded-lg py-2"
                        >
                          {isProcessing ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                          Confirmar rejeição
                        </button>
                        <button
                          onClick={() => { setRejectingId(null); setRejectReason(''); }}
                          className="px-4 text-sm text-gray-500 hover:text-gray-900"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2 justify-end">
                      {/* Right-aligned and sized to their words. Aprovar used to be `flex-1`, so
                          on a wide screen it stretched into a banner — the control that commits
                          somebody's face to the gate, made the easiest thing to hit by accident. */}
                      <button
                        onClick={() => handleApprove(item)}
                        disabled={isProcessing}
                        className="flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-semibold rounded-lg py-2 px-4"
                      >
                        {isProcessing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                        Aprovar
                      </button>
                      <button
                        onClick={() => { setRejectingId(item.id); setRejectReason(''); }}
                        disabled={isProcessing}
                        className="flex items-center justify-center gap-1.5 border border-gray-300 hover:bg-gray-50 disabled:opacity-60 text-gray-700 text-sm font-semibold rounded-lg py-2 px-4"
                      >
                        <XCircle className="h-4 w-4" />
                        Rejeitar
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
      </div>
    </>
  );
}

/**
 * The drawer the Operational View opens from MoradoresSelfRegSidebar. Chrome only — the queue
 * itself is [IdentityPhotoRequestsList], shared with the Solicitações page.
 */
export default function IdentityPhotoRequestsPanel({
  open,
  customerId,
  onClose,
}: IdentityPhotoRequestsPanelProps) {
  const [reloadToken, setReloadToken] = useState(0);
  const [{ count, loading }, setListState] = useState({ count: 0, loading: false });
  // Identity, so the child's effect does not re-run on every render of this one.
  const onStateChange = useCallback(
    (state: { count: number; loading: boolean }) => setListState(state),
    []
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative h-full w-full max-w-xl bg-white shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-black/[0.08]">
          <div className="flex items-center gap-2">
            <Camera className="h-5 w-5 text-[#2462EB]" />
            <h2 className="font-semibold text-gray-900">Fotos de Identificação</h2>
            {count > 0 && (
              <span className="text-xs font-semibold bg-blue-50 text-blue-700 rounded-full px-2 py-0.5">
                {count}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setReloadToken(t => t + 1)}
              disabled={loading}
              className="p-2 text-gray-500 hover:text-[#1C1C1E] transition-colors"
              title="Atualizar"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={onClose} className="p-2 text-gray-500 hover:text-[#1C1C1E] transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <p className="px-5 pt-3 text-xs text-gray-500 leading-relaxed">
          A foto de identificação é a que os leitores faciais usam. A foto de perfil do aplicativo
          é outra e não muda aqui.
        </p>

        <div className="flex-1 overflow-y-auto p-5">
          <IdentityPhotoRequestsList
            customerId={customerId}
            active={open}
            reloadToken={reloadToken}
            onStateChange={onStateChange}
          />
        </div>
      </div>
    </div>
  );
}
